import classNames from 'classnames';

import { PricelistProductDisplayOptionsData } from '../../../app/data/model';
import { ProductPricelistTranslation } from '../../localization/ModuleTranslation';
import type { PricelistPriceGroupData } from '../../model';
import Badge from '../../ui/Badge/Badge';
import Price from '../../ui/Price/Price';
import Skeleton from '../../ui/Skeleton/Skeleton';
import { Caption, Cell, HeaderCell, Row, Table, TableBody, TableHead } from '../../ui/Table/Table';
import NetPrice from '../NetPrice/NetPrice';
import css from './ProductPricelist.css';

interface ProductPricelistProps {
  displayOptions: PricelistProductDisplayOptionsData;
  priceGroups: PricelistPriceGroupData[];
  translation: ProductPricelistTranslation;
  basePrice?: number;
  netBasePrice?: number;
  className?: string;
}

export const ProductPricelist = ({
  basePrice,
  className,
  priceGroups,
  translation,
  displayOptions,
  netBasePrice,
}: ProductPricelistProps) => {
  const hasPriceOnDemand = displayOptions.showPriceOnDemand;
  const hasBasePrice = !hasPriceOnDemand && !!basePrice;
  const hasPriceGroups = !hasPriceOnDemand && priceGroups.any();

  if (!hasBasePrice && !hasPriceGroups && !hasPriceOnDemand) {
    return null;
  }

  return (
    <div className={classNames(css.root, className)}>
      <Table className={hasPriceGroups ? css.withPriceGroups : css.withoutPriceGroups}>
        <Caption>{translation.heading}</Caption>
        <TableHead>
          <Row>
            {hasPriceGroups ? (
              <HeaderCell scope="col">{translation.priceGroup}</HeaderCell>
            ) : (
              <Cell className={css.filler} />
            )}
            {(hasBasePrice || hasPriceOnDemand) && (
              <HeaderCell scope="col">
                {hasPriceGroups ? <Badge>{translation.basePrice}</Badge> : translation.price}
              </HeaderCell>
            )}
            {hasPriceGroups &&
              priceGroups.map((pg, index) => (
                <HeaderCell scope="col" key={index}>
                  <Badge>{pg.name}</Badge>
                </HeaderCell>
              ))}
            {hasBasePrice && !hasPriceGroups && <HeaderCell scope="col">{translation.netPrice.label}</HeaderCell>}
          </Row>
        </TableHead>
        <TableBody>
          <Row>
            {hasPriceGroups ? (
              <HeaderCell scope="row">{translation.price}</HeaderCell>
            ) : (
              <Cell className={css.filler} />
            )}
            {(hasBasePrice || hasPriceOnDemand) && (
              <Cell>{hasPriceOnDemand ? translation.priceOnDemand : <Price amount={basePrice} />}</Cell>
            )}
            {hasPriceGroups &&
              priceGroups.map((pg, index) => (
                <Cell key={index}>
                  <Price amount={pg.listPrice} />
                </Cell>
              ))}
            {hasBasePrice && !hasPriceGroups && (
              <Cell>
                <NetPrice amount={netBasePrice} translation={translation.netPrice} />
              </Cell>
            )}
          </Row>
          {hasPriceGroups && (
            <Row>
              <HeaderCell scope="row">{translation.netPrice.label}</HeaderCell>
              {hasBasePrice && (
                <Cell>
                  <NetPrice amount={netBasePrice} translation={translation.netPrice} />
                </Cell>
              )}
              {priceGroups.map((pg, index) => (
                <Cell key={index}>
                  <NetPrice amount={pg.netPrice} translation={translation.netPrice} />
                </Cell>
              ))}
            </Row>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

/** The row label plus the price groups a placeholder table holds space for. */
const SKELETON_COLUMN_COUNT = 8;

interface ProductPricelistSkeletonProps {
  translation: ProductPricelistTranslation;
  className?: string;
}

export const ProductPricelistSkeleton = ({ className, translation }: ProductPricelistSkeletonProps) => {
  const columns = Array.from({ length: SKELETON_COLUMN_COUNT }, (_: unknown, index: number) => index);

  return (
    <div className={classNames(css.root, className)}>
      <Table>
        <Caption>{translation.heading}</Caption>
        <TableHead>
          <Row className={css.headerRowSkeleton}>
            {columns.map((index) => (
              <HeaderCell key={index}>
                <Skeleton className={css.cellSkeleton} />
              </HeaderCell>
            ))}
          </Row>
        </TableHead>
        <TableBody>
          <Row className={css.rowSkeleton}>
            {columns.map((index) => (
              <Cell key={index}>
                <Skeleton className={css.cellSkeleton} />
              </Cell>
            ))}
          </Row>
        </TableBody>
      </Table>
    </div>
  );
};
